import * as React from 'react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DialogBodyProps } from '@fluentui/react-dialog'
import useAxios from '../../../../hooks/useAxios'
import {
    Button,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogTrigger,
    Divider,
    InteractionTag,
    InteractionTagPrimary,
    mergeClasses,
    Text,
    Tooltip,
} from '@fluentui/react-components'
import { HammockDialogProps } from '../HammockDialog'
import { Destination } from '../../../../context/AxiosContext'
import Loading from '../../../../components/Loading'
import { ExtendedEmailOverviewDto } from '../../../pages/File/ViewFileTabs/FileEmailOverview'
import { Attachment, Message, Recipient } from '@microsoft/microsoft-graph-types'
// @ts-expect-error("dompurify exports differ from what ts-lint is expecting and cannot see the default export")
import DOMPurify from 'dompurify'
import FormattedDateTime from '../../../../components/Date/FormattedDateTime'
import { useApplicationStyles } from '../../../../pages/App'
import useAppToast from '../../../../hooks/useAppToast'
import { ArrowLeftFilled, ArrowRightFilled, bundleIcon, DismissFilled, DismissRegular, OpenFilled, OpenRegular } from '@fluentui/react-icons'
import useOpenDownloadMessage from '../../../hooks/useOpenDownloadMessage'
import LoadingIcon from '../../../../components/Icon/LoadingIcon'

const ArrowLeftIcon = bundleIcon(ArrowLeftFilled, ArrowLeftFilled)
const ArrowRightIcon = bundleIcon(ArrowRightFilled, ArrowRightFilled)
const CloseIcon = bundleIcon(DismissFilled, DismissRegular)
const OpenIcon = bundleIcon(OpenFilled, OpenRegular)

export type PreviewMessageDialogBodyProps = DialogBodyProps & {
    message: ExtendedEmailOverviewDto
    destination: Destination
    hasPrevious?: boolean
    hasNext?: boolean
    previous?: () => void
    next?: () => void
}

export default function PreviewMessageDialogBody(props: HammockDialogProps<PreviewMessageDialogBodyProps>) {
    const axios = useAxios()
    const appStyles = useApplicationStyles()
    const { t: translate } = useTranslation()
    const toast = useAppToast()

    const dialogBodyProps = useMemo(() => props.dialogBodyProps?.() ?? ({} as PreviewMessageDialogBodyProps), [props])

    const [loading, setLoading] = useState<boolean>(false)
    const [fullMessage, setFullMessage] = useState<Message>(dialogBodyProps.message)
    const {
        setMessage: setOpeningMessage,
        loaded: openingMessageLoaded,
        isOpening,
        openOrDownload,
    } = useOpenDownloadMessage(undefined, dialogBodyProps.destination)

    const isHtmlBody = fullMessage?.body?.contentType?.toLowerCase() === 'html'
    const sanitizedHtmlBody = useMemo(() => {
        let result = isHtmlBody && fullMessage?.body?.content ? DOMPurify.sanitize(fullMessage.body.content) : fullMessage?.bodyPreview

        fullMessage?.attachments
            ?.filter(attachment => attachment['contentId'])
            .forEach((attachment: Attachment) => {
                const contentId = `cid:${attachment['contentId']}`
                const startIndex = result.indexOf(contentId)
                if (startIndex >= 0) {
                    result = result.replaceAll(
                        `cid:${attachment['contentId']}`,
                        `data:${attachment.contentType};base64, ${attachment['contentBytes']}`,
                    )
                }
            })

        return result
    }, [isHtmlBody, fullMessage])

    useEffect(() => {
        if (!dialogBodyProps.message) {
            setFullMessage(undefined)
            setOpeningMessage(undefined)
            setLoading(false)
            return
        }

        setLoading(true)
        axios
            .get(`/messages/${dialogBodyProps?.message?.id ?? fullMessage?.id}`, {
                baseURL: dialogBodyProps.destination,
                headers: { SuppressErrors: 'true', 'If-None-Match': null, Prefer: 'IdType="ImmutableId", outlook.body-content-type="html"' },
                params: {
                    $select:
                        'id,internetMessageId,conversationId,subject,bodyPreview,body,attachments,webLink,hasAttachments,sender,toRecipients,ccRecipients,bccRecipients,sentDateTime,receivedDateTime',
                    $expand: 'attachments',
                },
            })
            .then(response => {
                setFullMessage(response.data)
                setOpeningMessage(response.data)
                setLoading(false)
            })
    }, [dialogBodyProps.message])

    if (!dialogBodyProps.message) {
        return <></>
    }

    return (
        <DialogBody className={mergeClasses(appStyles.overflowXHidden, appStyles.height100)}>
            <DialogContent className={appStyles.overflowXHidden}>
                <div className={mergeClasses(appStyles.displayFlex, appStyles.gapS, appStyles.flexColumn)}>
                    <Text weight={'semibold'}>{fullMessage?.subject ?? translate('file.pivot.email.preview.button')}</Text>
                    <div className={mergeClasses(appStyles.displayFlex, appStyles.gapS, appStyles.flexColumn)}>
                        <div className={mergeClasses(appStyles.displayFlex, appStyles.spaceBetween)}>
                            <div className={mergeClasses(appStyles.displayFlex, appStyles.gapS)}>
                                {translate('file.pivot.email.preview.from')}:{' '}
                                <InteractionTag
                                    shape="rounded"
                                    size="small"
                                    onClick={() => {
                                        navigator.clipboard.writeText(`${fullMessage?.sender?.emailAddress?.address}`)
                                        toast.showMessage(translate('file.pivot.email.preview.copied'), { timeout: 1000 })
                                    }}
                                >
                                    <InteractionTagPrimary>
                                        {fullMessage?.sender?.emailAddress?.name || translate('base.unknown')}
                                    </InteractionTagPrimary>
                                </InteractionTag>
                            </div>
                            <div>{fullMessage?.sentDateTime && <FormattedDateTime date={fullMessage?.sentDateTime} />}</div>
                        </div>
                        <div>
                            {translate('file.pivot.email.preview.to')}:{' '}
                            {fullMessage?.toRecipients?.map((recipient: Recipient, index: number) => (
                                <InteractionTag
                                    shape="rounded"
                                    key={index}
                                    size="small"
                                    onClick={() => {
                                        navigator.clipboard.writeText(`${recipient?.emailAddress?.address}`)
                                        toast.showMessage(translate('file.pivot.email.preview.copied'), { timeout: 1000 })
                                    }}
                                >
                                    <InteractionTagPrimary>{recipient?.emailAddress?.name || translate('base.unknown')}</InteractionTagPrimary>
                                </InteractionTag>
                            ))}
                        </div>
                        {fullMessage?.ccRecipients?.length > 0 && (
                            <div>
                                {translate('file.pivot.email.preview.cc')}:{' '}
                                {fullMessage?.ccRecipients?.map((recipient: Recipient, index: number) => (
                                    <InteractionTag
                                        shape="rounded"
                                        key={index}
                                        size="small"
                                        onClick={() => {
                                            navigator.clipboard.writeText(`${recipient?.emailAddress?.address}`)
                                            toast.showMessage(translate('file.pivot.email.preview.copied'), { timeout: 1000 })
                                        }}
                                    >
                                        <InteractionTagPrimary>{recipient?.emailAddress?.name || translate('base.unknown')}</InteractionTagPrimary>
                                    </InteractionTag>
                                ))}
                            </div>
                        )}
                        {fullMessage?.bccRecipients?.length > 0 && (
                            <div>
                                {translate('file.pivot.email.preview.bcc')}:{' '}
                                {fullMessage?.bccRecipients?.map((recipient: Recipient, index: number) => (
                                    <InteractionTag
                                        shape="rounded"
                                        key={index}
                                        size="small"
                                        onClick={() => {
                                            navigator.clipboard.writeText(`${recipient?.emailAddress?.address}`)
                                            toast.showMessage(translate('file.pivot.email.preview.copied'), { timeout: 1000 })
                                        }}
                                    >
                                        <InteractionTagPrimary>{recipient?.emailAddress?.name || translate('base.unknown')}</InteractionTagPrimary>
                                    </InteractionTag>
                                ))}
                            </div>
                        )}
                        {fullMessage?.hasAttachments && (
                            <div className={mergeClasses(appStyles.displayFlex, appStyles.gapS, appStyles.flexWrap)}>
                                {translate('file.pivot.email.preview.attachments')}:{' '}
                                {fullMessage?.attachments?.map((attachment: Attachment, index: number) => (
                                    <InteractionTag shape="rounded" key={index} size="small">
                                        <InteractionTagPrimary>{attachment?.name || translate('base.unknown')}</InteractionTagPrimary>
                                    </InteractionTag>
                                ))}
                            </div>
                        )}
                    </div>
                    <Divider />
                    {!fullMessage && <Loading />}
                    {fullMessage && isHtmlBody && (
                        <div
                            dangerouslySetInnerHTML={{
                                __html: sanitizedHtmlBody,
                            }}
                        ></div>
                    )}
                    {fullMessage && !isHtmlBody && <div style={{ whiteSpace: 'pre-wrap' }}>{fullMessage?.body?.content || ''}</div>}
                </div>
            </DialogContent>
            <DialogActions>
                <div className={mergeClasses(appStyles.displayFlex, appStyles.width100, appStyles.gapS, appStyles.spaceBetween)}>
                    <Tooltip relationship={'label'} withArrow content={translate('file.pivot.email.open.button')}>
                        <Button
                            appearance="subtle"
                            disabled={loading || !openingMessageLoaded || isOpening}
                            icon={isOpening ? <LoadingIcon /> : <OpenIcon />}
                            onClick={e => {
                                e.stopPropagation()
                                openOrDownload()
                            }}
                        />
                    </Tooltip>
                    <div className={mergeClasses(appStyles.displayFlex, appStyles.gapS)}>
                        <Button
                            appearance="subtle"
                            disabled={loading || !dialogBodyProps.hasPrevious}
                            icon={<ArrowLeftIcon />}
                            onClick={() => dialogBodyProps.previous()}
                        />
                        <Button
                            appearance="subtle"
                            disabled={loading || !dialogBodyProps.hasNext}
                            icon={<ArrowRightIcon />}
                            onClick={() => dialogBodyProps.next()}
                        />
                    </div>

                    <DialogTrigger disableButtonEnhancement>
                        <Button appearance="secondary" icon={<CloseIcon />} />
                    </DialogTrigger>
                </div>
            </DialogActions>
        </DialogBody>
    )
}
